<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import CModal from '../CModal.vue';
import CButton from '@/components/shared/CButton';
import CBadge from '@/components/shared/CBadge/CBadge.vue';
import CInput from '@/components/shared/CInput.vue';
import CTextarea from '@/components/shared/CTextarea.vue';
import CEmptyState from '@/components/shared/CEmptyState.vue';

interface VariantOption {
    name: string;
    values: Record<'name' | 'hint', string>[];
    isPrintSizeVariant?: boolean;
    isColorOptionVariant?: boolean;
    readOnlyValues?: boolean;
}

interface ProductData {
    variants: VariantOption[];
    printSizeVariant?: VariantOption;
    description?: string;
    partialSave?: boolean;
}

const props = withDefaults(
    defineProps<{
        variants: VariantOption[];
        displayDescription?: boolean;
        canAddPrintSize?: boolean;
        canAddVariantOptions?: boolean;
    }>(),
    { canAddPrintSize: true, displayDescription: true }
);

const emit = defineEmits<{
    (event: 'save-product', data: ProductData): void;
    (event: 'close'): void;
}>();

const model = reactive({
    description: '',
    variantOptions: [] as VariantOption[]
});

const variantFormRef = ref<InstanceType<typeof ValidationObserver> | null>(null);
const isValidForm = () => variantFormRef.value?.validate();

const selectedVariant = ref<number | undefined>();
const editVariantOption = async (optionIdx?: number) => {
    if (!(await isValidForm())) return;

    selectedVariant.value = optionIdx;
};

const displayAddOptionBtn = computed(
    () => props.canAddVariantOptions && model.variantOptions.filter((o) => !o.isPrintSizeVariant).length < 2
);
const addVariantOption = async () => {
    if (!(await isValidForm())) return;

    model.variantOptions.push({
        name: '',
        values: [{ name: '', hint: '' }]
    });
    editVariantOption(model.variantOptions.length - 1);
};

const saveVariantOption = async () => {
    if (!(await isValidForm())) return false;
    editVariantOption();
};

const deleteVariantOption = (index: number) => {
    model.variantOptions.splice(index, 1);
    editVariantOption();
};

const onVariantValueChange = async (
    val: string | number,
    optionValue: Record<'name' | 'hint', string>,
    option: VariantOption
) => {
    const values = String(val).split(',');
    optionValue.name = values.shift() || '';

    if (!values.length) return;
    await addVariantValue(option, values);
};

const addVariantValue = async (option: VariantOption, optionValues?: string[]) => {
    if (option.isPrintSizeVariant || option.readOnlyValues) return;

    const values = (optionValues || ['']).map((val) => ({ name: val, hint: '' }));
    option.values.push(...values);

    await nextTick();
    const newInput = document.getElementById(
        `${option.name}-option-value-${option.values.length - 1}`
    ) as HTMLInputElement;
    newInput?.focus();
};

const deleteVariantValue = (option: VariantOption, index: number) => {
    option.values.splice(index, 1);
};

const saveProduct = async (partial = false) => {
    const valid = await saveVariantOption();
    if (valid === false) return;

    emit('save-product', {
        variants: model.variantOptions,
        description: model.description,
        partialSave: partial
    });
};

onMounted(() => {
    model.variantOptions = [...props.variants];
});
</script>

<template>
    <c-modal value size="lg" title="Product base variants" hide-close>
        <validation-observer ref="variantFormRef" tag="div" class="variants-modal-wrapper">
            <c-empty-state
                v-if="!model.variantOptions.length"
                class="empty-message"
                icon-class="icon-adjusted"
                message="This product will be created without variant options. You can add one by clicking the button below."
            />
            <div
                v-for="(option, optionIdx) in model.variantOptions"
                :key="`option-${optionIdx}`"
                class="variant-option-container border"
                :class="{ 'preview-mode': optionIdx !== selectedVariant }"
            >
                <template v-if="optionIdx !== selectedVariant">
                    <div class="flex-fill" @click="editVariantOption(optionIdx)">
                        <span class="option-title">{{ option.name }}</span>
                        <div class="option-values">
                            <c-badge
                                v-for="(value, valueIdx) in option.values"
                                :key="`value-${option.name}-${valueIdx}`"
                                color="cyan"
                            >
                                {{ value.name }}
                                <span v-if="value.hint.length" class="font-weight-normal">({{ value.hint }})</span>
                            </c-badge>
                        </div>
                    </div>
                    <c-button :icon="['far', 'pen']" @click="editVariantOption(optionIdx)" />
                </template>
                <template v-else>
                    <span class="option-title">Editing Variant Options</span>
                    <validation-provider v-slot="{ failed }" rules="required" slim>
                        <c-input
                            v-model="option.name"
                            required
                            :label="
                                option.isPrintSizeVariant ? 'Print size variant option name' : 'Variant option name'
                            "
                            :error="failed"
                            error-msg="You must write a name for the variant option."
                        />
                    </validation-provider>
                    <div class="option-values">
                        <validation-provider
                            v-for="(value, valueIdx) in option.values"
                            :key="`value-${option.name}-${valueIdx}`"
                            v-slot="{ failed, failedRules: { noDuplicate } }"
                            tag="div"
                            class="row align-items-center"
                            :rules="{
                                required: true,
                                noDuplicate: {
                                    values: option.values,
                                    key: 'name',
                                    index: valueIdx
                                }
                            }"
                        >
                            <c-input
                                :id="`${option.name}-option-value-${valueIdx}`"
                                :value="value.name"
                                class="col"
                                required
                                :disabled="option.readOnlyValues"
                                :label="valueIdx === 0 ? 'Variant option value' : ''"
                                :error="failed"
                                :error-msg="noDuplicate || 'You must add a value for the variant option.'"
                                @enter="addVariantValue(option)"
                                @input="onVariantValueChange($event, value, option)"
                            >
                                <template
                                    v-if="
                                        option.values.length > 1 && !option.isPrintSizeVariant && !option.readOnlyValues
                                    "
                                    #append
                                >
                                    <c-button
                                        :icon="['far', 'trash-can']"
                                        size="sm"
                                        @click="deleteVariantValue(option, valueIdx)"
                                    />
                                </template>
                            </c-input>
                            <div v-if="value.hint.length" class="col-4 font-italic" :class="{ 'mt-4': valueIdx === 0 }">
                                {{ value.hint }}
                            </div>
                        </validation-provider>
                        <template v-if="!option.isPrintSizeVariant && !option.readOnlyValues">
                            <c-button
                                variant="secondary"
                                text="Add another value"
                                :icon="['far', 'plus']"
                                @click="addVariantValue(option)"
                            />
                            <span>You can add values quickly by pressing “enter” when writing.</span>
                        </template>
                    </div>
                    <div class="d-flex justify-content-between align-items-center">
                        <c-button
                            v-if="!option.isPrintSizeVariant"
                            variant="outline-danger"
                            text="Delete"
                            size="sm"
                            :icon="['far', 'trash-can']"
                            @click="deleteVariantOption(optionIdx)"
                        />
                        <c-button
                            class="ml-auto"
                            variant="outline-primary"
                            text="Done"
                            size="sm"
                            @click="saveVariantOption"
                        />
                    </div>
                </template>
            </div>
            <c-button
                v-if="displayAddOptionBtn"
                variant="outline-primary"
                text="Add another variant option"
                :icon="['far', 'plus']"
                @click="addVariantOption"
            />
            <c-textarea
                v-if="displayDescription"
                v-model="model.description"
                label="Product description (optional)"
                placeholder="Add a description for this product..."
            />
        </validation-observer>
        <template #modal-footer>
            <div class="d-flex justify-content-end align-items-center w-100">
                <c-button
                    v-if="canAddPrintSize"
                    variant="outline-primary"
                    text="Save and add another print size"
                    @click="saveProduct(true)"
                />
                <c-button variant="primary" text="Save Product" @click="saveProduct()" />
            </div>
        </template>
    </c-modal>
</template>

<style lang="scss" scoped>
@import 'styles/utilities/functions';

.variants-modal-wrapper {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    margin: 0.25rem 0;
    .empty-message {
        font-size: 0.875rem;
        padding: 1.25rem 0;
    }
    .variant-option-container {
        display: flex;
        flex-direction: column;
        padding: 0.825rem 1rem;
        border-radius: 0.5rem;
        gap: 0.825rem;
        background-color: get-color('gray-lightest');
        &.preview-mode {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            cursor: pointer;
            background-color: unset;
            .btn {
                visibility: hidden;
            }
            &:hover {
                background-color: get-color('gray-lightest');
                .btn {
                    visibility: visible;
                }
            }
            .option-values {
                font-size: 1.15rem;
                flex-wrap: wrap;
                flex-direction: row;
                gap: 0.625rem;
                margin-top: 0.825rem;
            }
        }
        .option-title {
            font-size: 0.875rem;
            font-weight: bold;
        }
        .option-values {
            display: flex;
            flex-direction: column;
            gap: 0.375rem;
        }
    }
}
</style>
